import * as THREE from 'three';
import * as topojson from 'topojson-client';
import * as d3 from 'd3-geo';

export class ThreeUtils {
  // 经纬度转3D坐标（球面坐标）
  static latLonToVector3(lat, lon, radius = 5) {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    
    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const z = radius * Math.sin(phi) * Math.sin(theta);
    const y = radius * Math.cos(phi);
    
    return new THREE.Vector3(x, y, z);
  }

  //经纬度坐标转换为3D球体表面坐标（球坐标转直角坐标）
  static lngLatToXYZ(lng, lat, radius= 5.02) {
      const theta = (90 + lng) * (Math.PI / 180);
      const phi = (90 - lat) * (Math.PI / 180);
      return new THREE.Vector3().setFromSpherical(new THREE.Spherical(radius, phi, theta));
    }

  // 创建星空背景
  static createStarfield(starCount = 5000) {
    const geometry = new THREE.BufferGeometry();
    const positions = [];
    const colors = [];
    
    for (let i = 0; i < starCount; i++) {
      // 随机位置（球面分布）
      const radius = 100;
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      
      const x = radius * Math.sin(theta) * Math.cos(phi);
      const y = radius * Math.sin(theta) * Math.sin(phi);
      const z = radius * Math.cos(theta);
      
      positions.push(x, y, z);
      
      // 随机颜色（主要是白色和蓝色）
      colors.push(1, 1, Math.random() * 0.5 + 0.5);
    }
    
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    
    const material = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 0.8
    });
    
    return new THREE.Points(geometry, material);
  }

  // 创建国家边界
  static createCountryBorders(countryData, radius = 5.02) {
    const group = new THREE.Group();
    
    // 解析TopoJSON数据
    const countries = topojson.feature(countryData, countryData.objects.countries);
    
    // 创建材质
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0x4a90e2,
      transparent: true,
      opacity: 0.6,
      linewidth: 1
    });
    
    countries.features.forEach((feature, index) => {
      // 处理MultiPolygon和Polygon
      let geometries = feature.geometry.coordinates;
      if (feature.geometry.type === 'MultiPolygon') {
        geometries = feature.geometry.coordinates;
      } else if (feature.geometry.type === 'Polygon') {
        geometries = [feature.geometry.coordinates];
      }
      
      geometries.forEach(polygons => {
        polygons.forEach(polygon => {
          const points = [];
          
          polygon.forEach(coord => {
            const [lon, lat] = coord;
            // 经纬度转换为3D坐标
            const vector = this.latLonToVector3(lat, lon, radius);
            points.push(vector);
          });
          
          if (points.length > 0) {
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const line = new THREE.Line(geometry, lineMaterial);
            group.add(line);
          }
        });
      });
    });
    
    return group;
  }

  // 创建国家填充面（3D版）
  static createCountryFills(countryData, radius = 5.01) {
    const group = new THREE.Group();
    
    // 创建投影
    const projection = d3.geoOrthographic()
      .scale(radius * 50) // 调整缩放
      .translate([0, 0]);
    
    const path = d3.geoPath().projection(projection);
    
    // 解析数据
    const countries = topojson.feature(countryData, countryData.objects.countries);
    
    countries.features.forEach((feature, index) => {
      // 随机颜色
      const color = new THREE.Color(
        Math.random() * 0.2 + 0.6,  // R
        Math.random() * 0.3 + 0.4,  // G
        Math.random() * 0.2 + 0.5   // B
      );
      
      // 获取SVG路径数据
      const pathData = path(feature);
      if (!pathData) return;
      
      // 使用ExtrudeGeometry创建3D国家面
      const shape = this.svgPathToShape(pathData);
      if (!shape) return;
      
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: 0.01, // 很薄的3D效果
        bevelEnabled: false
      });
      
      geometry.scale(0.02, 0.02, 1); // 缩放调整
      
      const material = new THREE.MeshPhongMaterial({
        color: color,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = -Math.PI / 2; // 调整方向
      
      // 添加国家标签
      if (feature.properties && feature.properties.name) {
        const centroid = d3.geoCentroid(feature);
        const position = this.latLonToVector3(centroid[1], centroid[0], radius + 0.05);
        // this.addCountryLabel(feature.properties.name, position, color, group);
      }
      
      group.add(mesh);
    });
    
    return group;
  }

  // SVG路径转Three.js Shape
  static svgPathToShape(pathData) {
    if (!pathData) return null;
    
    const commands = pathData.match(/[MmLlHhVvCcSsQqTtAaZz]|[^MmLlHhVvCcSsQqTtAaZz]+/g);
    if (!commands) return null;
    
    const shape = new THREE.Shape();
    let currentX = 0;
    let currentY = 0;
    
    for (let i = 0; i < commands.length; i++) {
      const command = commands[i];
      const nextCommand = commands[i + 1];
      
      if (command === 'M' || command === 'm') {
        const coords = nextCommand.trim().split(/[\s,]+/).map(Number);
        if (coords.length >= 2) {
          if (command === 'M') {
            currentX = coords[0];
            currentY = coords[1];
          } else {
            currentX += coords[0];
            currentY += coords[1];
          }
          shape.moveTo(currentX, currentY);
          i++;
        }
      } else if (command === 'L' || command === 'l') {
        const coords = nextCommand.trim().split(/[\s,]+/).map(Number);
        if (coords.length >= 2) {
          if (command === 'L') {
            currentX = coords[0];
            currentY = coords[1];
          } else {
            currentX += coords[0];
            currentY += coords[1];
          }
          shape.lineTo(currentX, currentY);
          i++;
        }
      } else if (command === 'Z' || command === 'z') {
        shape.closePath();
      }
    }
    
    return shape;
  }

  // 添加国家标签
   static addCountryLabel(text, position, color, group) {
    // 创建Canvas绘制文字
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 128;
    
    context.fillStyle = `rgba(${Math.floor(color.r * 255)}, ${Math.floor(color.g * 255)}, ${Math.floor(color.b * 255)}, 0.8)`;
    context.font = 'bold 30px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, 128, 64);
    
    // 创建纹理
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    // 创建精灵
    const spriteMaterial = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true,
      opacity: 0.9
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    const normal = position.clone().normalize();
    // 将精灵沿着法线方向稍微抬高一点，避免与地面重叠
    const labelPosition = position.clone().add(normal.clone().multiplyScalar(0.05));
    sprite.position.copy(labelPosition);
    // 设置精灵朝向，使其始终面向相机
    sprite.material.rotation = 0;
    sprite.scale.set(0.5, 0.25, 1);
    
    group.add(sprite);
  }

  // 创建国家高亮效果
  static createCountryHighlight(countryData, radius = 5.02) {
    const group = new THREE.Group();
    
    // 创建发光材质
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3
    });
    
    // 这里可以根据需要高亮特定国家
    // 例如高亮中国
    const chinaFeature = countryData.features?.find(f => 
      f.properties?.name === 'China' || 
      f.properties?.name === '中国'
    );
    
    if (chinaFeature) {
      const coordinates = chinaFeature.geometry.coordinates;
      
      coordinates.forEach(polygon => {
        const vertices = [];
        
        polygon.forEach(coord => {
          const [lon, lat] = coord;
          const vector = this.latLonToVector3(lat, lon, radius);
          vertices.push(vector);
        });
        
        if (vertices.length > 2) {
          const geometry = new THREE.BufferGeometry().setFromPoints(vertices);
          const mesh = new THREE.Mesh(geometry, material);
          group.add(mesh);
        }
      });
    }
    
    return group;
  }

  // 增强的飞线效果
  static createEnhancedFlyLine(startLat, startLon, endLat, endLon, color = 0x0dfdf8, scene) {
    // 将起止点的经纬度转换为3D坐标
    const startPos = this.latLonToVector3(startLat, startLon, 5.1);
    const endPos = this.latLonToVector3(endLat, endLon, 5.1);
    
    // 创建更平滑的曲线
    const midHeight = Math.max(startPos.y, endPos.y) + 5;
    const controlPoint = new THREE.Vector3(
      (startPos.x + endPos.x) / 2,
      midHeight,
      (startPos.z + endPos.z) / 2
    );
    
    const curve = new THREE.QuadraticBezierCurve3(startPos, controlPoint, endPos);
    
    // 创建主飞线
    const points = curve.getPoints(100);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    
    // 使用发光材质
    const material = new THREE.LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.3,
      linewidth: 2
    });
    
    const line = new THREE.Line(geometry, material);
    
    // 创建轨迹光环
    const glowGeometry = new THREE.TubeGeometry(curve, 100, 0.05, 8, false);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.1,
      side: THREE.DoubleSide
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    
    // 创建移动物体（粒子）
    const particles = this.createMovingParticles(curve, color);
    
    // 添加到场景
    scene.add(line);
    scene.add(glow);
    particles.forEach(particle => scene.add(particle.mesh));
    
    return {
      line,
      glow,
      particles,
      curve,
      color,
      progress: 0,
      speed: 0.002
    };
  }

  // 创建移动粒子
  static createMovingParticles(curve, color) {
    const particles = [];
    const particleCount = 5; // 多个粒子
    
    for (let i = 0; i < particleCount; i++) {
      // 创建粒子几何体
      const geometry = new THREE.SphereGeometry(0.05, 16, 16);
      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: 0.8
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      
      // 添加拖尾效果
      const trail = this.createParticleTrail(color);
      
      particles.push({
        mesh,
        trail,
        offset: i / particleCount, // 分散位置
        speed: 0.002 + Math.random() * 0.001
      });
    }
    
    return particles;
  }

  // 创建粒子拖尾
  static createParticleTrail(color) {
    const trail = new THREE.Group();
    const trailLength = 5;
    
    for (let i = 0; i < trailLength; i++) {
      const geometry = new THREE.SphereGeometry(0.03, 8, 8);
      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: (trailLength - i) * 0.1
      });
      
      const sphere = new THREE.Mesh(geometry, material);
      trail.add(sphere);
    }
    
    return trail;
  }

  // ThreeUtils类中新增2D相关方法
  /**
   * 创建2D平面国家填充
   * @param {Object} countryData - topojson格式的国家数据
   * @param {Object} mapConfig - 平面地图配置
   * @returns {THREE.Group} 包含所有国家填充的组
   */
  // 在ThreeUtils类中修复createCountryFills2D方法
  static createCountryFills2D(countryData, mapConfig) {
    const group = new THREE.Group();
    
    // 优化墨卡托投影参数
    const projection = d3.geoMercator()
      .center([mapConfig.centerLon, mapConfig.centerLat])
      .scale(mapConfig.width * mapConfig.scale)
      .translate([mapConfig.width / 2, mapConfig.height / 2]);
    
    const path = d3.geoPath().projection(projection);
    const countries = topojson.feature(countryData, countryData.objects.countries);
    
    countries.features.forEach((feature) => {
      const pathData = path(feature);
      if (!pathData) return;
      
      const shape = this.svgPathToShape(pathData);
      if (!shape || shape.curves.length === 0) return;
      
      const geometry = new THREE.ShapeGeometry(shape);
      // 关键转换：匹配地图图片坐标
      geometry.scale(1, -1, 1); // 纠正y轴方向
      geometry.translate(-mapConfig.width / 2, -mapConfig.height / 2, 0.01); // 对齐中心
      
      // 随机颜色（与3D保持一致）
      const color = new THREE.Color(
        Math.random() * 0.2 + 0.6,
        Math.random() * 0.3 + 0.4,
        Math.random() * 0.2 + 0.5
      );
      
      // 创建材质（半透明叠加）
      const material = new THREE.MeshPhongMaterial({
        color: color,
        transparent: true,
        opacity: 0.4, // 降低透明度，突出地图背景
        side: THREE.DoubleSide,
        depthWrite: false // 避免遮挡地图
      });
      
      const mesh = new THREE.Mesh(geometry, material);
      group.add(mesh);

      // 添加2D标签
      if (feature.properties?.name) {
        const centroid = d3.geoCentroid(feature);
        if (centroid) {
          const [x, y] = projection(centroid);
          const position = new THREE.Vector3(
            x - mapConfig.width / 2,
            0.02, // 抬高避免遮挡
            -(y - mapConfig.height / 2)
          );
          this.addCountryLabel2D(feature.properties.name, position, color, group);
        }
      }
    });
    
    return group;
  }

    /**
     * 创建2D平面国家边界
     * @param {Object} countryData - topojson格式的国家数据
     * @param {Object} mapConfig - 平面地图配置
     * @returns {THREE.Group} 包含所有国家边界的组
     */
  static createCountryBorders2D(countryData, mapConfig) {
    const group = new THREE.Group();
    
    const projection = d3.geoMercator()
      .center([mapConfig.centerLon, mapConfig.centerLat])
      .scale(mapConfig.width * mapConfig.scale)
      .translate([mapConfig.width / 2, mapConfig.height / 2]);
    
    const path = d3.geoPath().projection(projection);
    const countries = topojson.feature(countryData, countryData.objects.countries);
    
    countries.features.forEach((feature) => {
      const pathData = path(feature);
      if (!pathData) return;
      
      const points = this.svgPathToPoints(pathData, mapConfig);
      if (points.length < 2) return;
      
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x333333,
        linewidth: 1,
        transparent: true,
        opacity: 0.8,
        depthTest: true // 确保边界在标签下方
      });
      
      const line = new THREE.Line(geometry, material);
      group.add(line);
    });
    
    return group;
  }

    /**
     * SVG路径转换为Three.js点数组（用于2D边界）
     * @param {string} pathData - SVG路径字符串
     * @param {Object} mapConfig - 地图配置
     * @returns {THREE.Vector3[]} 点数组
     */
  static svgPathToPoints(pathData, mapConfig) {
    const points = [];
    const commands = pathData.match(/[MmLlHhVvCcSsQqTtAaZz]|[^MmLlHhVvCcSsQqTtAaZz]+/g);
    if (!commands) return points;
    
    let currentX = 0;
    let currentY = 0;
    
    for (let i = 0; i < commands.length; i++) {
      const command = commands[i];
      const nextCommand = commands[i + 1];
      
      if (command === 'M' || command === 'm') {
        const coords = nextCommand.trim().split(/[\s,]+/).map(Number);
        if (coords.length >= 2) {
          currentX = command === 'M' ? coords[0] : currentX + coords[0];
          currentY = command === 'M' ? coords[1] : currentY + coords[1];
          // 直接映射到平面本地坐标
          points.push(new THREE.Vector3(
            currentX - mapConfig.width / 2,
            -(currentY - mapConfig.height / 2),
            0.03 // 边界层级高于标签
          ));
          i++;
        }
      } else if (command === 'L' || command === 'l') {
        const coords = nextCommand.trim().split(/[\s,]+/).map(Number);
        if (coords.length >= 2) {
          currentX = command === 'L' ? coords[0] : currentX + coords[0];
          currentY = command === 'L' ? coords[1] : currentY + coords[1];
          points.push(new THREE.Vector3(
            currentX - mapConfig.width / 2,
            -(currentY - mapConfig.height / 2),
            0.03
          ));
          i++;
        }
      }
    }
    
    return points;
  }

    /**
     * 添加2D模式的国家标签（优化平面显示）
     * @param {string} text - 国家名称
     * @param {THREE.Vector3} position - 标签位置
     * @param {THREE.Color} color - 文字颜色
     * @param {THREE.Group} group - 父容器
     */
  static addCountryLabel2D(text, position, color, group) {
    // 只显示大国标签（避免过多重叠）
    const largeCountries = ['China', 'United States', 'Russia', 'India', 'Brazil'];
    if (!largeCountries.includes(text)) return;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 150; // 缩小标签尺寸
    canvas.height = 60;
    
    context.fillStyle = `rgba(${Math.floor(color.r * 255)}, ${Math.floor(color.g * 255)}, ${Math.floor(color.b * 255)}, 1)`;
    context.font = 'bold 16px Arial'; // 缩小字体
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, 75, 30);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 1
    });
    
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(position);
    sprite.scale.set(0.5, 0.25, 1); // 缩小标签缩放比例
    group.add(sprite);
  }

}
